'use client'; // This directive is for Next.js and can be removed for standard React (CRA)

import React, { createContext, useContext, useState } from 'react';
import Cookies from 'js-cookie';

// Import all your translation files
import en from '../translations/en.json';
import nl from '../translations/nl.json';
import es from '../translations/es.json';
import it from '../translations/it.json';
import ae from '../translations/ae.json';

const translations = { en, nl, es, it, ae };

/**
 * @typedef {('en' | 'nl' | 'es' | 'it' | 'ae')} Locale
 */

/**
 * @typedef {object} LanguageContextType
 * @property {Locale} locale - The current active locale.
 * @property {(newLocale: Locale) => void} changeLocale - Function to change the current locale.
 * @property {(key: string) => string} t - The translation function.
 */

/**
 * React Context for managing language settings.
 * @type {React.Context<LanguageContextType | undefined>}
 */
const LanguageContext = createContext(undefined);

/**
 * The provider component that wraps your app to provide language context.
 * @param {object} props
 * @param {React.ReactNode} props.children - The child components.
 * @param {Locale} props.initialLocale - The initial language for the app.
 */
export function LanguageProvider({ children, initialLocale }) {
	const [locale, setLocale] = useState(initialLocale);

	const changeLocale = (newLocale) => {
		Cookies.set('locale', newLocale, { expires: 365 });
		setLocale(newLocale);
		// Reload the page to ensure the entire app uses the new language
		window.location.reload();
	};

	const t = (key) => {
		const keys = key.split('.');
		let result = translations[locale];
		for (const k of keys) {
			// Use optional chaining for safety
			result = result?.[k];
			if (result === undefined) {
				// Return the key itself if not found, to easily spot missing translations
				return key;
			}
		}
		return result;
	};

	return (
		<LanguageContext.Provider value={{ locale, changeLocale, t }}>
			{children}
		</LanguageContext.Provider>
	);
}

/**
 * Custom hook to easily access the language context.
 * @returns {LanguageContextType}
 */
export function useLanguage() {
	const context = useContext(LanguageContext);
	if (!context) {
		throw new Error('useLanguage must be used within a LanguageProvider');
	}
	return context;
}
